import {
  Box,
  Container,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Flex,
  Badge,
  Heading,
  Link,
  // eslint-disable-next-line no-restricted-imports
  Button as ChakraButton,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { RevealOnScroll } from '../../../atoms/RevealOnScroll';
import { Icon } from 'src/components/atoms/Icon';
import { useState, useMemo } from 'react';
import { useContactModal } from 'src/hooks/useContactModal';
import { ContactModal } from 'src/components/molecules/ContactModal';
import { FAQ_CATEGORIES, FAQ_DATA } from 'src/features/MP/utils/constants';

const FaqSupportPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedItems, setExpandedItems] = useState<number[]>([1, 6]); // Start with first and billing question expanded

  const { onOpen, modalProps } = useContactModal({
    defaultInterest: 'pricing',
    title: 'Start Modeling',
    submitButtonText: 'Start Modeling',
  });

  const { onOpen: onOpenSales, modalProps: salesModalProps } = useContactModal({
    defaultInterest: 'sales',
    title: 'Contact Sales',
    submitButtonText: 'Contact Sales',
  });

  const filteredFAQs = useMemo(() => {
    let filtered = FAQ_DATA;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((faq) => faq.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory]);

  const toggleExpanded = (index: number) => {
    setExpandedItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  return (
    <>
      <Box bg="white" color="text.darkBlue.900" fontFamily="marketingPage">
        <Container maxW="container.xl" pt={{ base: 8, md: 16 }}>
          <RevealOnScroll>
            {/* Breadcrumb */}
            <HStack
              spacing={4}
              mb={4}
              fontSize="16px"
              color="text.darkBlue.800"
              fontWeight="500">
              <Link
                as={RouterLink}
                to="/resources"
                opacity="0.6"
                _hover={{ opacity: 1, textDecoration: 'underline' }}
                cursor="pointer">
                Resources & Docs
              </Link>
              <Text>/</Text>
              <Text>FAQ & Support</Text>
            </HStack>

            {/* Header */}
            <VStack align="start" spacing={4} mb={{ base: 12, md: 24 }}>
              <Text fontSize={{ base: '24px', md: '40px' }} fontWeight="700">
                FAQ & SUPPORT
              </Text>
              <Text
                fontSize="15px"
                color="text.darkBlue.800"
                fontFamily="marketingPage"
                fontWeight="normal">
                Your go-to place for quick answers, guides, and direct help from
                our team.
              </Text>
            </VStack>
          </RevealOnScroll>

          <RevealOnScroll>
            {/* Search Bar */}
            <Box mb={10}>
              <InputGroup maxW="512px">
                <InputLeftElement pointerEvents="none" h="52px">
                  <Icon iconName="search" />
                </InputLeftElement>
                <Input
                  placeholder="Search common questions, features, or issues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="white"
                  h="52px"
                  border="1px solid"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: 'brand.primary',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-primary)',
                  }}
                />
              </InputGroup>
            </Box>

            {/* Category Tabs */}
            <Box mb={8} borderBottom="1px solid" borderColor="gray.200">
              <HStack
                w="full"
                justify="space-between"
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                }}>
                {FAQ_CATEGORIES.map((category) => (
                  <Text
                    key={category}
                    fontSize="md"
                    fontWeight="medium"
                    color={
                      selectedCategory === category
                        ? 'text.darkBlue.900'
                        : 'text.darkBlue.600'
                    }
                    cursor="pointer"
                    pb={2}
                    px={7}
                    borderBottom={
                      selectedCategory === category ? '2px solid' : 'none'
                    }
                    borderColor={
                      selectedCategory === category
                        ? 'icon.green'
                        : 'transparent'
                    }
                    whiteSpace="nowrap"
                    _hover={{
                      color: 'text.darkBlue.900',
                    }}
                    onClick={() => setSelectedCategory(category)}>
                    {category}
                  </Text>
                ))}
              </HStack>
            </Box>

            {/* FAQ List */}
            <VStack spacing={4} align="stretch" mb={32}>
              {filteredFAQs.map((faq) => (
                <Box
                  key={faq.id}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="16px"
                  overflow="hidden">
                  <Flex
                    justify="space-between"
                    p={6}
                    cursor="pointer"
                    onClick={() => toggleExpanded(faq.id)}
                    _hover={{ bg: 'gray.50' }}>
                    <VStack align="start" spacing={2} flex={1}>
                      <Text
                        fontSize="lg"
                        fontWeight="semibold"
                        color="text.darkBlue.900">
                        {faq.question}
                      </Text>
                      <Badge
                        colorScheme="white"
                        variant="subtle"
                        fontSize="12px"
                        fontFamily="marketingPage"
                        fontWeight="500"
                        color="text.darkBlue.900"
                        opacity={0.6}
                        padding={0}
                        textTransform="uppercase">
                        {faq.category}
                      </Badge>
                    </VStack>
                    <Box ml={4}>
                      <Icon
                        iconName={
                          expandedItems.includes(faq.id) ? 'minus' : 'plus'
                        }
                        color="gray.500"
                      />
                    </Box>
                  </Flex>
                  {expandedItems.includes(faq.id) && (
                    <Box px={6} pb={6}>
                      <Text
                        fontSize="md"
                        color="text.darkBlue.700"
                        lineHeight="1.6"
                        fontFamily="marketingPage">
                        {faq.answer}
                      </Text>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          </RevealOnScroll>
        </Container>
      </Box>

      <RevealOnScroll>
        {/* Call to Action Section */}
        <VStack spacing={4} px={4} textAlign="center" mb={48}>
          <Heading
            color={'text.darkBlue.900'}
            fontSize={{ base: '2xl', md: '3xl', lg: '4.5xl' }}
            textTransform="uppercase"
            fontFamily="marketingPage">
            Ready to get real answers <br /> from your data?
          </Heading>
          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            color="text.darkBlue.800"
            fontWeight={500}>
            Start modeling with AI-powered insights or talk to <br />
            our team to find the best setup for your business.
          </Text>
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            gap={4}
            w={{ base: '100%', sm: 'auto' }}>
            <ChakraButton
              onClick={onOpen}
              size="lg"
              bg={'icon.green'}
              color="white"
              fontWeight="bold"
              fontFamily="marketingPage"
              borderRadius="8px"
              px={8}
              w={{ base: '100%', sm: 'auto' }}
              _hover={{ bg: 'icon.green.500' }}>
              Start modeling
            </ChakraButton>
            <ChakraButton
              onClick={onOpenSales}
              size="lg"
              bg={'background.lightGrey'}
              border="1px solid #F0F0F0"
              color="text.darkBlue.900"
              fontFamily="marketingPage"
              borderRadius={8}
              px={8}
              py={4}
              w={{ base: '100%', sm: 'auto' }}>
              Contact Sales &nbsp; →
            </ChakraButton>
          </Flex>
        </VStack>
      </RevealOnScroll>
      <ContactModal {...modalProps} />
      <ContactModal {...salesModalProps} />
    </>
  );
};

export default FaqSupportPage;
